import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Card from "../components/ui/Card";

export default function HowItWorks() {
  return (
    <>
      <Helmet>
        <title>How It Works | UKPropertyChecks</title>
        <meta
          name="description"
          content="How UKPropertyChecks works. Buyer's Report covers EPC, flood, heritage, planning, and tax in three steps. Seller's Pack prepares TA6, TA7, and LPE1 forms in four."
        />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/how-it-works" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to prepare selling documents for a UK property",
            description:
              "Step-by-step process for preparing solicitor-ready TA6, TA7, and LPE1 forms for a UK property sale using UKPropertyChecks.",
            totalTime: "PT30M",
            estimatedCost: {
              "@type": "MonetaryAmount",
              currency: "GBP",
              value: "79.00",
            },
            step: [
              {
                "@type": "HowToStep",
                position: 1,
                name: "Confirm property basics",
                text: "Enter the property address, confirm tenure (freehold or leasehold), and provide basic property details.",
                url: "https://ukpropertychecks.co.uk/",
              },
              {
                "@type": "HowToStep",
                position: 2,
                name: "Complete the disclosure questionnaire",
                text: "Answer the guided disclosure questions that mirror the official TA6 (59 questions), TA7 (26 questions, leasehold only), and LPE1 (22 questions, leasehold only) forms. You can mark questions as 'Not known' if unsure.",
                url: "https://ukpropertychecks.co.uk/",
              },
              {
                "@type": "HowToStep",
                position: 3,
                name: "Upload supporting documents",
                text: "Upload documents such as the title register, EPC certificate, planning permissions, guarantees, and lease agreement (if leasehold). Documents are processed to extract relevant field values with a source trail.",
                url: "https://ukpropertychecks.co.uk/",
              },
              {
                "@type": "HowToStep",
                position: 4,
                name: "Review and pay",
                text: "Preview the draft forms, review any reformatted answers, pay £79 via Stripe, and download the solicitor-ready PDF pack.",
                url: "https://ukpropertychecks.co.uk/",
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to run a Buyer's Report on a UK property",
            description:
              "Step-by-step process for ordering a Buyer's Report covering Energy Performance Certificate (EPC), flood risk, heritage designations, alteration constraints, planning history, and tax/regulatory rules for a UK property.",
            totalTime: "PT5M",
            estimatedCost: {
              "@type": "MonetaryAmount",
              currency: "GBP",
              value: "29",
            },
            step: [
              {
                "@type": "HowToStep",
                position: 1,
                name: "Enter the property address",
                text: "Enter a UK postcode and select the address from the lookup results.",
                url: "https://ukpropertychecks.co.uk/",
              },
              {
                "@type": "HowToStep",
                position: 2,
                name: "Preview the property file",
                text: "See a free summary of public records for the property — what's on file before you pay.",
                url: "https://ukpropertychecks.co.uk/",
              },
              {
                "@type": "HowToStep",
                position: 3,
                name: "Pay and download the full Buyer's Report",
                text: "Pay £29 via Stripe and download the full Buyer's Report PDF. The report has eight sections covering EPC, flood risk, heritage designations, alteration constraints (Green Belt, AONB, TPO, Article 4), planning history, and tax/regulatory rules. Title summary is omitted until HM Land Registry integration is live.",
                url: "https://ukpropertychecks.co.uk/",
              },
            ],
          })}
        </script>
      </Helmet>

      <main className="bg-white">
        <div className="container-main section-spacing">
          <h1>How it works</h1>
          <p className="mt-3 text-slate-800">
            Two products, one workflow. Pick what you need and follow the steps.
          </p>

          <Card className="mt-8">
            <h2 className="text-lg font-semibold text-slate-950">Buyer's Report — three steps</h2>
            <ol className="mt-4 space-y-6">
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">1</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Enter the property address</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Search by postcode and pick the property from the list.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">2</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Preview the property file</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Free summary of public records — see what's on file before you pay.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">3</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Pay £29 and download the PDF</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Eight sections covering EPC, flood, heritage, alterations, planning history, and tax rules. No account required to buy — you can save it to an account afterwards.
                  </p>
                </div>
              </li>
            </ol>
          </Card>

          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-950">Seller's Pack — four steps</h2>
            <ol className="mt-4 space-y-6">
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">1</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Confirm property basics</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Address and tenure. Tenure (freehold or leasehold) decides which forms apply.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">2</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Answer the disclosure questionnaire</p>
                  <p className="mt-1 text-sm text-slate-700">
                    The same questions your solicitor would ask: TA6 (59), TA7 (26, leasehold), LPE1 (22, leasehold). Mark anything you don't know — your solicitor can follow up.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">3</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Upload supporting documents</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Title register, lease, service-charge statements, EPC, planning permissions. We extract relevant fields with a source trail.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">4</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Review, pay £79, download</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Preview the filled forms, then pay to download the solicitor-ready PDF pack.
                  </p>
                </div>
              </li>
            </ol>
          </Card>

          <Card className="mt-6">
            <p className="text-sm text-slate-700">
              Information and document preparation only. Not legal advice.
            </p>
            <div className="mt-4 text-sm text-slate-700">
              Ready to start?{" "}
              <Link to="/" className="text-blue-700 hover:text-blue-900 underline">
                Run a Buyer's Report
              </Link>{" "}
              or{" "}
              <Link to="/" className="text-blue-700 hover:text-blue-900 underline">
                prepare a Seller's Pack
              </Link>
              .
            </div>
            <div className="mt-2 text-sm text-slate-700">
              <Link to="/samples" className="text-blue-700 hover:text-blue-900 underline">
                View sample reports
              </Link>{" "}
              to see what you receive.
            </div>
          </Card>
        </div>
      </main>
    </>
  );
}
